import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { AiOutlineFilePdf, AiOutlineGithub } from "react-icons/ai";
import { SiArxiv } from "react-icons/si";
import { BiLinkExternal } from "react-icons/bi";
import researchFig from "../../Assets/research-fig2.png";
import Particle from "../Particle";
import "./Research.css";

const PAPER = {
  title:
    "Calibrated Enough to Know, Not Calibrated to Act: Fabricated Evidence Makes LLM Agents Commit to the Unknowable",
  meta: "Sole author · 2026 · arXiv preprint",
  links: [
    { label: "arXiv", href: "https://arxiv.org/abs/2608.27167", icon: SiArxiv },
    { label: "PDF", href: "https://arxiv.org/pdf/2608.27167", icon: AiOutlineFilePdf },
    { label: "Code & Data", href: "https://github.com/Pranav-1100/confidence-calibration-evaluation", icon: AiOutlineGithub },
    { label: "DOI", href: "https://doi.org/10.5281/zenodo.21325374", icon: BiLinkExternal },
  ],
  findings: [
    {
      stat: "6.5% → 54%",
      text: "Across 12 frontier models, a professional-looking data panel makes AI agents answer questions nobody can know.",
    },
    {
      stat: "36.8% vs 37.6%",
      text: "A panel where every number is made up works just as well as real data. The trigger is how official it looks.",
    },
    {
      stat: "0.0%",
      text: "A small 3B model trained on dice and coin examples learns to decline instead, and carries that to new domains.",
    },
  ],
};

function Research() {
  return (
    <Container fluid className="research-section" id="research">
      <Container>
        <h1 className="project-heading" style={{ paddingBottom: "10px" }}>
          My <strong className="purple">Research</strong>
        </h1>
        <p className="research-intro">
          I study when AI agents act on information they cannot actually use, and whether that can be trained away.
        </p>

        <div className="research-card">
          <Row className="align-items-center">
            <Col lg={7}>
              <h3 className="research-title">{PAPER.title}</h3>
              <p className="research-meta">{PAPER.meta}</p>

              <div className="research-findings">
                {PAPER.findings.map((f) => (
                  <div className="research-finding" key={f.stat}>
                    <span className="research-stat">{f.stat}</span>
                    <span className="research-finding-text">{f.text}</span>
                  </div>
                ))}
              </div>

              <p className="research-learned">
                <strong>What I learned:</strong> checking what a model says about its own confidence is not enough.
                To catch this kind of failure, you have to look at what the model actually does.
              </p>

              <div className="research-links">
                {PAPER.links.map(({ label, href, icon: Icon }) => (
                  <a key={label} href={href} target="_blank" rel="noreferrer" className="research-link">
                    <Icon /> {label}
                  </a>
                ))}
              </div>
            </Col>
            <Col lg={5} className="mt-4 mt-lg-0">
              <a href="https://arxiv.org/abs/2608.27167" target="_blank" rel="noreferrer">
                <img src={researchFig} alt="Figure from the paper: commitment with real versus fabricated evidence panels" className="research-figure" />
              </a>
              <p className="research-caption">Fabricated panels move agents as far as real ones.</p>
            </Col>
          </Row>
        </div>
      </Container>
    </Container>
  );
}

export function ResearchPage() {
  return (
    <Container fluid className="research-page">
      <Particle />
      <Research />
    </Container>
  );
}

export default Research;
